import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [blurIntensity, setBlurIntensity] = useState(0);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });

      // Gradually increase blur as video plays
      const handleTimeUpdate = () => {
        if (videoRef.current) {
          const duration = videoRef.current.duration;
          const currentTime = videoRef.current.currentTime;
          const progress = currentTime / duration;
          
          // Start blurring at 30% of video, reach max blur at 70%
          if (progress > 0.3) {
            const blurProgress = Math.min((progress - 0.3) / 0.4, 1);
            setBlurIntensity(blurProgress * 8); // Max 8px blur
          }
        }
      };

      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        }
      };
    }
  }, []);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          className={`w-full h-full object-cover transition-opacity duration-1000 ${
            isVideoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setIsVideoLoaded(true)}
          style={{
            filter: `blur(${blurIntensity}px)`
          }}
        >
          <source
            src="/videos/logo_animation.mp4"
            type="video/mp4"
          />
        </video>
        
        {/* Overlay gradient - darker for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        
        {/* Additional blur overlay that fades in */}
        <div 
          className="absolute inset-0 backdrop-blur-sm transition-opacity duration-1000"
          style={{
            opacity: blurIntensity / 8
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight drop-shadow-2xl animate-fade-in-up">
          <span className="inline">luxury </span>
          <span className="inline">through </span>
          <span
            className="inline text-[hsl(45,100%,70%)]"
            style={{
              textShadow: '0 0 40px rgba(255, 215, 0, 0.5), 0 4px 20px rgba(0, 0, 0, 0.8)'
            }}
          >
            artistry
          </span>
        </h1>
        
        <p className="text-lg md:text-xl text-gray-100 max-w-2xl mb-12 animate-fade-in-up animation-delay-600 drop-shadow-lg">
          Unique sculptures handcrafted with love for detail. Strictly limited collections for people with a sense of style and design.
        </p>

        <button
          onClick={scrollToContent}
          className="animate-fade-in-up animation-delay-800"
        >
          <div className="w-16 h-16 rounded-full border-2 border-[hsl(45,100%,70%)] flex items-center justify-center hover:bg-[hsl(45,100%,70%)] hover:scale-110 transition-all duration-300 group">
            <ChevronDown className="w-8 h-8 text-[hsl(45,100%,70%)] group-hover:text-black transition-colors animate-bounce" />
          </div>
        </button>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out both;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animation-delay-800 {
          animation-delay: 0.8s;
        }
      `}</style>
    </div>
  );
};

export default HeroSection;